import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { format, subDays, startOfDay, endOfDay, addHours, parseISO, startOfMonth, endOfMonth, startOfWeek, endOfWeek } from 'date-fns';
import { Settings, ChevronUp, ChevronDown, Users, DollarSign, Clock, ChevronLeft, ChevronRight, Plus, X, Search, Trash2, CreditCard, RefreshCw, MapPin, LogOut, ArrowLeft, Loader2, Car, Repeat, UserCheck, XCircle, Pencil } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import Footer from './Footer';
import { useLot } from '../contexts/LotContext';
import Logo from './marketing/Logo';

type DashboardView = 'all' | 'parking' | 'subscriptions' | 'free_parkers';

interface ParkingSession {
  id: string;
  license_plate: string;
  phone: string;
  hours: number;
  amount: number;
  payment_status: string;
  created_at: string;
  expires_at: string;
  card_last4?: string;
  payment_method_type?: string;
  payment_id?: string;
  transaction_id?: string;
  location: string;
  session_type?: 'New' | 'Extension' | 'Monthly' | 'Free' | 'Hourly';
  notes?: string;
}


interface DailyRevenue {
  date: string;
  revenue: number;
}

interface AccessibleLot {
  lot_id: string;
  role: string;
  lot_slug: string;
  lot_display_name: string;
  lot_status: string;
}

type TimeRange = '7' | '30' | '365';

function AdminDashboard() {
  const navigate = useNavigate();
  const { lotSlug } = useParams<{ lotSlug: string }>();
  const { lot, settings, userRole, isLoading, error } = useLot();
  const [sessions, setSessions] = useState<ParkingSession[]>([]);
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<TimeRange>('7');
  const [currentView, setCurrentView] = useState<DashboardView>('all');
  const [revenueData, setRevenueData] = useState<DailyRevenue[]>([]);
  const [stats, setStats] = useState({
    activeParkers: 0,
    todayRevenue: 0,
    monthlyRevenue: 0,
    lastMonthRevenue: 0,
    expiringCount: 0,
    // Subscription-specific stats (amount >= 100, not Yolo)
    activeSubscriptions: 0,
    subscriptionTodayRevenue: 0,
    subscriptionMonthlyRevenue: 0,
    subscriptionLastMonthRevenue: 0,
    // Parking-only stats (amount < 100, not Yolo)
    parkingActiveParkers: 0,
    parkingTodayRevenue: 0,
    parkingMonthlyRevenue: 0,
    parkingLastMonthRevenue: 0,
    // Free parker stats (Yolo)
    freeParkerCount: 0,
    activeFreeParkers: 0
  });
  const [serverPagination, setServerPagination] = useState({
    currentPage: 1,
    pageSize: 100,
    totalRecords: 0,
    totalPages: 0
  });
  const [totalSessions, setTotalSessions] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isSorting, setIsSorting] = useState(false);
  const [isPaginating, setIsPaginating] = useState(false);
  const [statusFilters, setStatusFilters] = useState<Set<string>>(new Set());
  const [isFiltering, setIsFiltering] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const searchExecutionRef = useRef<NodeJS.Timeout | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const executeSearchReadyRef = useRef<boolean>(false);
  const lastRefreshTriggerRef = useRef<number>(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState<ParkingSession | null>(null);
  const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);
  const [sessionToRefund, setSessionToRefund] = useState<ParkingSession | null>(null);
  const [isCancelSubModalOpen, setIsCancelSubModalOpen] = useState(false);
  const [sessionToCancelSub, setSessionToCancelSub] = useState<ParkingSession | null>(null);
  const [cancelSubMode, setCancelSubMode] = useState<'end_of_period' | 'immediately' | 'immediately_refund'>('end_of_period');
  const [cancelSubProcessing, setCancelSubProcessing] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [sessionToEdit, setSessionToEdit] = useState<ParkingSession | null>(null);
  const [editLicensePlate, setEditLicensePlate] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [newSession, setNewSession] = useState({
    license_plate: '',
    location: lot?.display_name || '',
    notes: ''
  });

  // Lot switcher state
  const [accessibleLots, setAccessibleLots] = useState<AccessibleLot[]>([]);
  const [showLotDropdown, setShowLotDropdown] = useState(false);
  const [loadingLots, setLoadingLots] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'Invalid Date';
    try {
      return format(parseISO(dateString), 'PPpp');
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid Date';
    }
  };

  // Check user authentication and lot access
  useEffect(() => {
    const checkAuthentication = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/admin');
        return;
      }

      // Only redirect if lot context has finished loading and there's still no lot
      if (!isLoading && !lot) {
        navigate('/admin');
        return;
      }

      // Only check user role if lot context has finished loading
      if (!isLoading && userRole === 'none') {
        toast.error('Access denied to this parking lot');
        navigate('/admin');
        return;
      }
    };

    checkAuthentication();
  }, [lot, userRole, isLoading, navigate]);

  // Fetch user's accessible lots
  const fetchAccessibleLots = useCallback(async () => {
    try {
      setLoadingLots(true);
      console.log('[AdminDashboard] Fetching accessible lots...');
      
      const { data: lotAccess, error } = await supabase.rpc('get_user_lot_access');
      
      if (error) {
        console.error('[AdminDashboard] Error fetching accessible lots:', error);
        return;
      }

      console.log('[AdminDashboard] Accessible lots data:', lotAccess);
      setAccessibleLots(lotAccess || []);
    } catch (error) {
      console.error('[AdminDashboard] Error in fetchAccessibleLots:', error);
    } finally {
      setLoadingLots(false);
    }
  }, []);

  // Handle lot switching
  const handleLotSwitch = (lotSlug: string) => {
    setShowLotDropdown(false);
    if (lotSlug !== lot?.slug) {
      navigate(`/admin/lots/${lotSlug}/dashboard`);
    }
  };

  useEffect(() => {
    const loadDashboard = async () => {
      // Only load if lot context has finished loading and user has access
      if (isLoading || !lot || userRole === 'none') return;
      
      // Load stats and accessible lots immediately - call functions directly
      try {
        // Fetch stats inline using dual-query approach for consistency
        const now = new Date();
        const todayStart = startOfDay(now);
        const todayEnd = endOfDay(now);
        const monthStart = startOfMonth(now);
        const monthEnd = endOfMonth(now);

        // Fetch accessible lots inline
        const accessibleLotsPromise = (async () => {
          try {
            setLoadingLots(true);
            const { data: lotAccess, error } = await supabase.rpc('get_user_lot_access');
            if (!error) {
              setAccessibleLots(lotAccess || []);
            }
          } catch (error) {
            console.error('[AdminDashboard] Error in fetchAccessibleLots:', error);
          } finally {
            setLoadingLots(false);
          }
        })();

        // Today's sessions - dual query for lot_id OR null lot_id with matching location
        const { data: todayWithLotId } = await supabase
          .from('parking_sessions')
          .select('*')
          .gte('created_at', todayStart.toISOString())
          .lte('created_at', todayEnd.toISOString())
          .eq('lot_id', lot.id);

        const { data: todayNullLotId } = await supabase
          .from('parking_sessions')
          .select('*')
          .gte('created_at', todayStart.toISOString())
          .lte('created_at', todayEnd.toISOString())
          .is('lot_id', null)
          .eq('location', lot.display_name);

        const todaySessions = [...(todayWithLotId || []), ...(todayNullLotId || [])];

        // This month's sessions - dual query
        const { data: monthWithLotId } = await supabase
          .from('parking_sessions')
          .select('*')
          .gte('created_at', monthStart.toISOString())
          .lte('created_at', monthEnd.toISOString())
          .eq('lot_id', lot.id);

        const { data: monthNullLotId } = await supabase
          .from('parking_sessions')
          .select('*')
          .gte('created_at', monthStart.toISOString())
          .lte('created_at', monthEnd.toISOString())
          .is('lot_id', null)
          .eq('location', lot.display_name);

        const monthSessions = [...(monthWithLotId || []), ...(monthNullLotId || [])];

        // All sessions for active parkers - dual query
        const { data: allWithLotId } = await supabase
          .from('parking_sessions')
          .select('*')
          .eq('lot_id', lot.id);

        const { data: allNullLotId } = await supabase
          .from('parking_sessions')
          .select('*')
          .is('lot_id', null)
          .eq('location', lot.display_name);

        const allSessions = [...(allWithLotId || []), ...(allNullLotId || [])];

        // Wait for accessible lots to finish loading
        await accessibleLotsPromise;

        // Calculate active parkers (non-expired sessions)
        const activeParkers = allSessions.filter(session => {
          if (!session.expires_at) return false;
          try {
            return parseISO(session.expires_at) > now;
          } catch {
            return false;
          }
        }).length;

        // Calculate revenue
        const todayRevenue = todaySessions.reduce((sum, session) => {
          const amount = parseFloat(session.amount || '0');
          return sum + amount;
        }, 0);

        const monthRevenue = monthSessions.reduce((sum, session) => {
          const amount = parseFloat(session.amount || '0');
          return sum + amount;
        }, 0);

        setStats({
          activeParkers,
          todayRevenue: todayRevenue,
          monthlyRevenue: monthRevenue,
          lastMonthRevenue: 0, // Simplified for now
          expiringCount: 0,
          // Set defaults - will be populated by fetchStats
          activeSubscriptions: 0,
          subscriptionTodayRevenue: 0,
          subscriptionMonthlyRevenue: 0,
          subscriptionLastMonthRevenue: 0,
          parkingActiveParkers: activeParkers,
          parkingTodayRevenue: todayRevenue,
          parkingMonthlyRevenue: monthRevenue,
          parkingLastMonthRevenue: 0,
          freeParkerCount: 0,
          activeFreeParkers: 0
        });
        
        // Stats with last month's revenue and subscription breakdown will be populated by fetchStats

      } catch (error) {
        toast.error('Failed to load dashboard data');
      }
    };

    loadDashboard();
  }, [lot, userRole, isLoading]);

  // Separate effect for loading sessions - trigger initial load
  useEffect(() => {
    if (!isLoading && lot && userRole !== 'none') {

      // Trigger the search useEffect to load sessions
      setRefreshTrigger(prev => prev + 1);
    }
  }, [lot, userRole, isLoading]);

  // Close dropdown when clicking outside or pressing Escape
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowLotDropdown(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowLotDropdown(false);
      }
    };

    if (showLotDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showLotDropdown]);

  // Stable search execution function
  const executeSearch = useCallback(async (searchTerm: string, resetToFirstPage = true, isFilterOperation = false) => {
    if (searchTerm.trim()) {
      try {
        if (isFilterOperation) {
          setIsFiltering(true);
        } else {
          setIsSearching(true);
        }
        
        const page = resetToFirstPage ? 1 : serverPagination.currentPage;
        const from = (page - 1) * serverPagination.pageSize;
        const to = from + serverPagination.pageSize - 1;
        
        // Remove all whitespace from search term for whitespace-insensitive license plate search
        const cleanSearchTerm = searchTerm.replace(/\s+/g, '');
        
        // License plate search - multiple patterns to handle whitespace variations
        // Since Supabase doesn't support complex SQL functions in URL params, use multiple simple conditions
        const searchConditions = [
          `license_plate.ilike.%${cleanSearchTerm}%`,
          `license_plate.ilike.%${cleanSearchTerm.split('').join(' ')}%`,
          `license_plate.ilike.%${cleanSearchTerm.split('').join('  ')}%`
        ];
        
        // Add common license plate patterns if the term has letters and numbers
        if (/[A-Za-z]/.test(cleanSearchTerm) && /[0-9]/.test(cleanSearchTerm)) {
          // Try adding space between letters and numbers (e.g., CMED468 -> CMED 468)
          const spaceAddedTerm = cleanSearchTerm.replace(/([A-Za-z]+)([0-9]+)/g, '$1 $2');
          if (spaceAddedTerm !== cleanSearchTerm) {
            searchConditions.push(`license_plate.ilike.%${spaceAddedTerm}%`);
          }
        }
        
        let query = supabase
          .from('parking_sessions')
          .select('*', { count: 'exact' })
          .order(sortField, { ascending: sortDirection === 'asc' })
          .range(from, to)
          .or(searchConditions.join(','));
        
        if (lot) {
          query = query.filter('lot_id', 'eq', lot.id);
        }
        
        // Apply status filters
        if (statusFilters.size > 0) {
          const filterConditions: string[] = [];
          if (statusFilters.has('active')) {
            filterConditions.push('expires_at.gte.' + new Date().toISOString());
          }
          if (statusFilters.has('expired')) {
            filterConditions.push('expires_at.lt.' + new Date().toISOString());
          }
          if (filterConditions.length > 0) {
            query = query.or(filterConditions.join(','));
          }
        }
        
        const { data, error, count } = await query;
        
        if (error) throw error;
        
        setSessions(data || []);
        setServerPagination(prev => ({
          ...prev,
          currentPage: page,
          totalRecords: count || 0,
          totalPages: Math.ceil((count || 0) / prev.pageSize)
        }));
        setTotalSessions(count || 0);
        
      } catch (error) {
        toast.error('Failed to search sessions');
      } finally {
        if (isFilterOperation) {
          setIsFiltering(false);
        } else {
          setIsSearching(false);
        }
      }
    } else {
      // Fetch all sessions (no search)
      try {
        setLoading(true);
        
        let countQuery = supabase
          .from('parking_sessions')
          .select('*', { count: 'exact', head: true });
        
        if (lot) {
          countQuery = countQuery.filter('lot_id', 'eq', lot.id);
        }
        
        // Apply status filters
        if (statusFilters.size > 0) {
          const filterConditions: string[] = [];
          if (statusFilters.has('active')) {
            filterConditions.push('expires_at.gte.' + new Date().toISOString());
          }
          if (statusFilters.has('expired')) {
            filterConditions.push('expires_at.lt.' + new Date().toISOString());
          }
          if (filterConditions.length > 0) {
            countQuery = countQuery.or(filterConditions.join(','));
          }
        }
        
        const { count } = await countQuery;
        
        setServerPagination(prev => ({
          ...prev,
          currentPage: 1,
          totalRecords: count || 0,
          totalPages: Math.ceil((count || 0) / prev.pageSize)
        }));
        setTotalSessions(count || 0);
        
        // Fetch first page
        let query = supabase
          .from('parking_sessions')
          .select('*')
          .order(sortField, { ascending: sortDirection === 'asc' })
          .range(0, serverPagination.pageSize - 1);
        
        if (lot) {
          query = query.filter('lot_id', 'eq', lot.id);
        }
        
        // Apply status filters
        if (statusFilters.size > 0) {
          const filterConditions: string[] = [];
          if (statusFilters.has('active')) {
            filterConditions.push('expires_at.gte.' + new Date().toISOString());
          }
          if (statusFilters.has('expired')) {
            filterConditions.push('expires_at.lt.' + new Date().toISOString());
          }
          if (filterConditions.length > 0) {
            query = query.or(filterConditions.join(','));
          }
        }
        
        const { data, error } = await query;
        
        if (error) throw error;
        
        setSessions(data || []);
        
      } catch (error) {
        toast.error('Failed to fetch parking sessions');
      } finally {
        if (isFilterOperation) {
          setIsFiltering(false);
        } else {
          setLoading(false);
        }
      }
    }
  }, [lot, sortField, sortDirection, statusFilters, serverPagination.pageSize, serverPagination.currentPage]);

  // Mark executeSearch as ready when it's defined
  useEffect(() => {
    executeSearchReadyRef.current = true;
  }, [executeSearch]);

  // Simple input handler - only updates state, no search
  const handleSearchInputChange = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  // Debounced search effect - execute immediately on refresh-triggered updates, debounce on typing
  useEffect(() => {
    // Clear existing timeouts
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    if (searchExecutionRef.current) {
      clearTimeout(searchExecutionRef.current);
    }

    const refreshTriggered = refreshTrigger !== lastRefreshTriggerRef.current;
    lastRefreshTriggerRef.current = refreshTrigger;

    const delay = refreshTriggered ? 0 : 500; // faster but non-blocking debounce
    const timeoutRef = refreshTriggered ? searchExecutionRef : searchTimeoutRef;

    timeoutRef.current = setTimeout(async () => {
      if (!lot || userRole === 'none') return;

      const trimmedQuery = searchQuery.trim();
      const pageSize = serverPagination.pageSize;
      // Snapshot whether this run was triggered by a filter toggle to avoid full-page spinner
      const filterRun = isFiltering;

      try {
        if (trimmedQuery) {
          setIsSearching(true);

          const from = 0;
          const to = pageSize - 1;

          // Remove all whitespace from search term for whitespace-insensitive license plate search
          const cleanSearchTerm = trimmedQuery.replace(/\s+/g, '');
          
          console.log('🔍 Search Debug:', { cleanSearchTerm });
          
          // License plate search - multiple patterns to handle whitespace variations
          // Since Supabase doesn't support complex SQL functions in URL params, use multiple simple conditions
          const searchConditions = [
            `license_plate.ilike.%${cleanSearchTerm}%`,
            `license_plate.ilike.%${cleanSearchTerm.split('').join(' ')}%`,
            `license_plate.ilike.%${cleanSearchTerm.split('').join('  ')}%`
          ];
          
          // Add common license plate patterns if the term has letters and numbers
          if (/[A-Za-z]/.test(cleanSearchTerm) && /[0-9]/.test(cleanSearchTerm)) {
            // Try adding space between letters and numbers (e.g., CMED468 -> CMED 468)
            const spaceAddedTerm = cleanSearchTerm.replace(/([A-Za-z]+)([0-9]+)/g, '$1 $2');
            if (spaceAddedTerm !== cleanSearchTerm) {
              searchConditions.push(`license_plate.ilike.%${spaceAddedTerm}%`);
            }
          }

          let query = supabase
            .from('parking_sessions')
            .select('*', { count: 'exact' })
            .order(sortField, { ascending: sortDirection === 'asc' })
            .range(from, to)
            .not('payment_status', 'is', null)
            .or(searchConditions.join(','));

          if (lot) {
            query = query.filter('lot_id', 'eq', lot.id);
          }

          // Apply view filter only for admin/owner users
          // Use simple filters (.eq, .gte, .lt, .neq) to avoid clobbering the search .or()
          const isAdminOrOwner = userRole === 'admin' || userRole === 'owner';
          if (isAdminOrOwner && currentView === 'free_parkers') {
            query = query.eq('payment_status', 'Yolo');
          } else if (isAdminOrOwner && currentView === 'subscriptions') {
            query = query.eq('payment_method_type', 'subscription');
          } else if (isAdminOrOwner && currentView === 'parking') {
            query = query.neq('payment_method_type', 'subscription').neq('payment_status', 'Yolo');
          }

          if (statusFilters.size > 0) {
            const filterConditions: string[] = [];
            if (statusFilters.has('active')) {
              filterConditions.push('expires_at.gte.' + new Date().toISOString());
            }
            if (statusFilters.has('expired')) {
              filterConditions.push('expires_at.lt.' + new Date().toISOString());
            }
            if (statusFilters.has('scheduled')) {
              filterConditions.push('and(scheduled_start_date.not.is.null,scheduled_start_date.gte.' + new Date().toISOString() + ')');
            }
            if (statusFilters.has('cancelled')) {
              filterConditions.push('subscription_cancelled_at.not.is.null');
            }
            if (filterConditions.length > 0) {
              query = query.or(filterConditions.join(','));
            }
          }

          const { data, error, count } = await query;
          if (error) throw error;

          setSessions(data || []);
          setServerPagination(prev => ({
            ...prev,
            currentPage: 1,
            totalRecords: count || 0,
            totalPages: Math.ceil((count || 0) / prev.pageSize)
          }));
          setTotalSessions(count || 0);
        } else {
          // Avoid full-page spinner during filter operations and input clears
          if (!filterRun) {
            if (refreshTriggered) {
              setLoading(true);
            } else {
              setIsSearching(true);
            }
          }

          let countQuery = supabase
            .from('parking_sessions')
            .select('*', { count: 'exact', head: true })
            .not('payment_status', 'is', null);
          if (lot) {
            countQuery = countQuery.filter('lot_id', 'eq', lot.id);
          }
          
          // Apply view filter only for admin/owner users
          const isAdminOrOwnerNoSearch = userRole === 'admin' || userRole === 'owner';
          if (isAdminOrOwnerNoSearch && currentView === 'free_parkers') {
            countQuery = countQuery.eq('payment_status', 'Yolo');
          } else if (isAdminOrOwnerNoSearch && currentView === 'subscriptions') {
            countQuery = countQuery.eq('payment_method_type', 'subscription');
          } else if (isAdminOrOwnerNoSearch && currentView === 'parking') {
            countQuery = countQuery.neq('payment_method_type', 'subscription').neq('payment_status', 'Yolo');
          }
          
          if (statusFilters.size > 0) {
            const filterConditions: string[] = [];
            if (statusFilters.has('active')) {
              filterConditions.push('expires_at.gte.' + new Date().toISOString());
            }
            if (statusFilters.has('expired')) {
              filterConditions.push('expires_at.lt.' + new Date().toISOString());
            }
            if (statusFilters.has('scheduled')) {
              filterConditions.push('and(scheduled_start_date.not.is.null,scheduled_start_date.gte.' + new Date().toISOString() + ')');
            }
            if (statusFilters.has('cancelled')) {
              filterConditions.push('subscription_cancelled_at.not.is.null');
            }
            if (filterConditions.length > 0) {
              countQuery = countQuery.or(filterConditions.join(','));
            }
          }

          const { count } = await countQuery;
          setServerPagination(prev => ({
            ...prev,
            currentPage: 1,
            totalRecords: count || 0,
            totalPages: Math.ceil((count || 0) / prev.pageSize)
          }));
          setTotalSessions(count || 0);

          let query = supabase
            .from('parking_sessions')
            .select('*')
            .order(sortField, { ascending: sortDirection === 'asc' })
            .range(0, pageSize - 1)
            .not('payment_status', 'is', null);
          if (lot) {
            query = query.filter('lot_id', 'eq', lot.id);
          }
          
          // Apply view filter only for admin/owner users
          if (isAdminOrOwnerNoSearch && currentView === 'free_parkers') {
            query = query.eq('payment_status', 'Yolo');
          } else if (isAdminOrOwnerNoSearch && currentView === 'subscriptions') {
            query = query.eq('payment_method_type', 'subscription');
          } else if (isAdminOrOwnerNoSearch && currentView === 'parking') {
            query = query.neq('payment_method_type', 'subscription').neq('payment_status', 'Yolo');
          }
          
          if (statusFilters.size > 0) {
            const filterConditions: string[] = [];
            if (statusFilters.has('active')) {
              filterConditions.push('expires_at.gte.' + new Date().toISOString());
            }
            if (statusFilters.has('expired')) {
              filterConditions.push('expires_at.lt.' + new Date().toISOString());
            }
            if (statusFilters.has('scheduled')) {
              filterConditions.push('and(scheduled_start_date.not.is.null,scheduled_start_date.gte.' + new Date().toISOString() + ')');
            }
            if (statusFilters.has('cancelled')) {
              filterConditions.push('subscription_cancelled_at.not.is.null');
            }
            if (filterConditions.length > 0) {
              query = query.or(filterConditions.join(','));
            }
          }

          const { data, error } = await query;
          if (error) throw error;

          setSessions(data || []);
        }
      } catch (_error) {
        if (searchQuery.trim()) {
          toast.error('Failed to search sessions');
        } else {
          toast.error('Failed to fetch parking sessions');
        }
      } finally {
        if (searchQuery.trim()) {
          setIsSearching(false);
        } else {
          if (filterRun) {
            setIsFiltering(false);
          } else if (refreshTriggered) {
            setLoading(false);
          } else {
            setIsSearching(false);
          }
        }
      }
    }, delay);

    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      if (searchExecutionRef.current) clearTimeout(searchExecutionRef.current);
    };
  }, [searchQuery, refreshTrigger, lot, userRole, sortField, sortDirection, statusFilters, serverPagination.pageSize, currentView]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Helper function to apply status filters to a query
  const applyStatusFilters = useCallback((query: any) => {
    if (statusFilters.size === 0) {
      return query; // No filters applied
    }

    const filterConditions: string[] = [];
    
    if (statusFilters.has('active')) {
      filterConditions.push('expires_at.gte.' + new Date().toISOString());
    }
    
    if (statusFilters.has('expired')) {
      filterConditions.push('expires_at.lt.' + new Date().toISOString());
    }

    if (statusFilters.has('scheduled')) {
      filterConditions.push('and(scheduled_start_date.not.is.null,scheduled_start_date.gte.' + new Date().toISOString() + ')');
    }

    if (statusFilters.has('cancelled')) {
      filterConditions.push('subscription_cancelled_at.not.is.null');
    }

    if (filterConditions.length > 0) {
      return query.or(filterConditions.join(','));
    }
    
    return query;
  }, [statusFilters]);


  // Handle status filter toggle
  const handleStatusFilterToggle = useCallback(async (status: string) => {
    // Show lightweight overlay instead of full-page loading
    setIsFiltering(true);
    setStatusFilters(prev => {
      const newFilters = new Set(prev);
      if (newFilters.has(status)) {
        newFilters.delete(status);
      } else {
        newFilters.add(status);
      }
      return newFilters;
    });
    // Data fetching is triggered by the statusFilters effect
  }, []);

  // Handle clear all filters
  const handleClearAllFilters = useCallback(async () => {
    // Show lightweight overlay instead of full-page loading
    setIsFiltering(true);
    setStatusFilters(new Set());
    // Data fetching is triggered by the statusFilters effect
  }, []);

  useEffect(() => {
    if (lot) {
      fetchRevenueData();
    }
  }, [timeRange, lot, currentView]);

  useEffect(() => {
    // Refresh data when sort changes - trigger refresh
    if (!lot || userRole === 'none') return;
    setRefreshTrigger(prev => prev + 1);
  }, [sortField, sortDirection, lot, userRole]);

  useEffect(() => {
    // Refresh data when status filters change - trigger refresh
    if (!lot || userRole === 'none') return;
    setRefreshTrigger(prev => prev + 1);
  }, [statusFilters, lot, userRole]);

  // Refresh data when view changes
  useEffect(() => {
    if (!lot || userRole === 'none') return;
    setRefreshTrigger(prev => prev + 1);
  }, [currentView, lot, userRole]);

  const calculatePercentageChange = (current: number, previous: number) => {
    if (previous === 0) {
      return current > 0 ? 100 : 0;
    }
    return ((current - previous) / previous) * 100;
  };

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate('/admin');
      return;
    }
  };

  const formatPhoneNumber = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return '(' + match[1] + ') ' + match[2] + '-' + match[3];
    }
    return cleaned;
  };

  const generateTransactionId = () => {
    return Math.random().toString(36).substr(2, 9).toUpperCase();
  };

  // Function to determine the session type label for display
  const determineSessionType = (session: ParkingSession, allSessions: ParkingSession[]): 'New' | 'Extension' | 'Monthly' | 'Free' | 'Hourly' => {
    if (session.session_type) {
      return session.session_type;
    }

    // Derive from session data for older rows that predate the parking-type labels
    if (session.payment_method_type === 'subscription') return 'Monthly';
    if (session.payment_status === 'Yolo') return 'Free';

    // Fall back to New / Extension heuristic for legacy hourly sessions
    const sessionDate = new Date(session.created_at);
    const oneDayBefore = new Date(sessionDate.getTime() - 24 * 60 * 60 * 1000);
    const previousSessions = allSessions.filter(s =>
      s.id !== session.id &&
      s.license_plate === session.license_plate &&
      s.location === session.location &&
      new Date(s.created_at) >= oneDayBefore &&
      new Date(s.created_at) < sessionDate &&
      (s.payment_status === 'paid' || s.payment_status === 'Yolo')
    );
    return previousSessions.length > 0 ? 'Extension' : 'New';
  };

  const handleAddSession = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // Validation
      if (!newSession.license_plate) {
        toast.error('Please enter a license plate');
        return;
      }
      
      // Create very far future date (10 years from now)
      const futureDate = new Date();
      futureDate.setFullYear(futureDate.getFullYear() + 10);
      
      // Set location - handle the special "all" case
      let sessionLocation = 'Louisa Street'; // Default fallback
      
      if (newSession.location === 'all') {
        sessionLocation = 'All Locations';
      } else if (newSession.location) {
        sessionLocation = newSession.location;
      } else if (lot) {
        sessionLocation = lot.display_name;
      }
        
      // Create the session
      const { error } = await supabase.from('parking_sessions').insert({
        license_plate: newSession.license_plate.toUpperCase(),
        phone: '0000000000',
        hours: 24,
        amount: 0,
        payment_status: 'Yolo',
        expires_at: futureDate.toISOString(),
        location: sessionLocation,
        lot_id: lot?.id, // Add lot_id for proper association
        auto_renew: false,
        payment_method_type: null,
        card_last4: null,
        session_type: 'Free',
        notes: newSession.notes,
      });

      if (error) {
        console.error('Error adding subscription:', error);
        throw error;
      }

      toast.success('Free parker added successfully');
      setIsModalOpen(false);
      setNewSession({ license_plate: '', location: '', notes: '' });
      fetchSessions(serverPagination.currentPage, serverPagination.pageSize);
      fetchStats();
    } catch (error) {
      toast.error('Failed to add free parker');
    }
  };

  const handleDeleteSession = async () => {
    if (!sessionToDelete) return;

    try {
      const { error } = await supabase
        .from('parking_sessions')
        .delete()
        .eq('id', sessionToDelete.id);

      if (error) throw error;

      toast.success('Session deleted successfully');
      setIsDeleteModalOpen(false);
      setSessionToDelete(null);
      fetchSessions(serverPagination.currentPage, serverPagination.pageSize);
      fetchStats();
    } catch (error) {
      toast.error('Failed to delete session');
    }
  };

  const handleRefund = async () => {
    if (!sessionToRefund) return;

    try {
      // Only proceed if we have a payment ID (Stripe payment or subscription)
      if (!sessionToRefund.payment_id) {
        throw new Error('No payment information available for this session');
      }

      // Call the refund API endpoint
      const response = await fetch('/.netlify/functions/refund-payment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId: sessionToRefund.id,
          paymentId: sessionToRefund.payment_id,
          paymentMethodType: sessionToRefund.payment_method_type || 'card'
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.details || 'Failed to process refund with Stripe');
      }

      // The Netlify function now handles the database update, so we don't need to do it here
      
      toast.success('Payment refunded successfully');
      setIsRefundModalOpen(false);
      setSessionToRefund(null);
      fetchSessions(serverPagination.currentPage, serverPagination.pageSize);
      fetchRevenueData();
      fetchStats();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to process refund';
      toast.error(errorMessage);
    }
  };

  const handleCancelSubscription = async () => {
    if (!sessionToCancelSub) return;

    try {
      setCancelSubProcessing(true);

      if (!sessionToCancelSub.payment_id) {
        throw new Error('No subscription ID available for this session');
      }

      const response = await fetch('/.netlify/functions/cancel-subscription', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: sessionToCancelSub.id,
          subscriptionId: sessionToCancelSub.payment_id,
          mode: cancelSubMode
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.details || 'Failed to cancel subscription');
      }

      const modeLabels: Record<string, string> = {
        end_of_period: 'Subscription scheduled to cancel at end of billing period',
        immediately: 'Subscription cancelled immediately',
        immediately_refund: 'Subscription cancelled and refund issued'
      };

      toast.success(modeLabels[cancelSubMode] || 'Subscription cancelled');
      setIsCancelSubModalOpen(false);
      setSessionToCancelSub(null);
      setCancelSubMode('end_of_period');
      fetchSessions(serverPagination.currentPage, serverPagination.pageSize);
      fetchRevenueData();
      fetchStats();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to cancel subscription';
      toast.error(errorMessage);
    } finally {
      setCancelSubProcessing(false);
    }
  };

  const handleEditSession = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sessionToEdit) return;

    const trimmedPlate = editLicensePlate.trim().toUpperCase();
    const trimmedPhone = editPhone.trim();

    if (!trimmedPlate) {
      toast.error('License plate cannot be empty');
      return;
    }

    try {
      setEditSaving(true);
      const { error } = await supabase
        .from('parking_sessions')
        .update({ license_plate: trimmedPlate, phone: trimmedPhone })
        .eq('id', sessionToEdit.id);

      if (error) throw error;

      toast.success('Session updated');
      setIsEditModalOpen(false);
      setSessionToEdit(null);
      fetchSessions(serverPagination.currentPage, serverPagination.pageSize);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to update session';
      toast.error(msg);
    } finally {
      setEditSaving(false);
    }
  };

  const fetchRevenueData = async () => {
    try {
      const startDate = subDays(new Date(), parseInt(timeRange));
      
      console.log('[Revenue] Fetching revenue data...', {
        startDate: startDate.toISOString(),
        timeRange,
        lotId: lot?.id,
        lotDisplayName: lot?.display_name
      });
      
      let allData: { created_at: string; amount: number }[] = [];
      const BATCH_SIZE = 1000;

      // Helper function to fetch all records in batches (Supabase limits to 1000 per query)
      const fetchAllBatches = async (
        buildQuery: () => ReturnType<typeof supabase.from<'parking_sessions'>>
      ): Promise<{ created_at: string; amount: number }[]> => {
        const results: { created_at: string; amount: number }[] = [];
        let offset = 0;
        let hasMore = true;

        while (hasMore) {
          const { data, error } = await buildQuery()
            .range(offset, offset + BATCH_SIZE - 1);

          if (error) throw error;

          if (data && data.length > 0) {
            results.push(...data);
            console.log(`[Revenue] Batch fetched: ${data.length} records (total so far: ${results.length})`);
            offset += BATCH_SIZE;
            hasMore = data.length === BATCH_SIZE;
          } else {
            hasMore = false;
          }
        }

        return results;
      };

      // Free parkers have no revenue — clear chart and exit early
      if (currentView === 'free_parkers') {
        setRevenueData([]);
        return;
      }

      // Helper to apply view-specific filter to a revenue query
      const addViewFilter = (q: ReturnType<typeof supabase.from>) => {
        if (currentView === 'subscriptions') return q.eq('payment_method_type', 'subscription');
        if (currentView === 'parking') return (q as any).neq('payment_method_type', 'subscription').neq('payment_status', 'Yolo');
        return q; // 'all' — no extra filter beyond payment_status = 'paid'
      };

      if (lot) {
        // Fetch all sessions with matching lot_id using batched queries
        allData = await fetchAllBatches(() =>
          addViewFilter(
            supabase
              .from('parking_sessions')
              .select('created_at, amount')
              .gte('created_at', startDate.toISOString())
              .eq('payment_status', 'paid')
              .eq('lot_id', lot.id)
              .order('created_at', { ascending: true })
          )
        );

        console.log('[Revenue] Total fetched:', allData.length, 'sessions');
      } else {
        // No lot filter - fetch all paid sessions in batches
        allData = await fetchAllBatches(() =>
          addViewFilter(
            supabase
              .from('parking_sessions')
              .select('created_at, amount')
              .gte('created_at', startDate.toISOString())
              .eq('payment_status', 'paid')
              .order('created_at', { ascending: true })
          )
        );
      }

      const data = allData;
      console.log('[Revenue] Total sessions:', data?.length || 0);
      console.log('[Revenue] Total amount: $' + (data?.reduce((sum, s) => sum + Number(s.amount), 0) || 0).toFixed(2));

      // Determine aggregation granularity: yearly→monthly, 30-day→weekly, 7-day→daily
      const aggregationType: 'monthly' | 'weekly' | 'daily' =
        timeRange === '365' ? 'monthly' : timeRange === '30' ? 'weekly' : 'daily';
      const aggregatedRevenue = new Map<string, number>();
      
      data?.forEach(session => {
        if (!session.created_at) return;
        try {
          const d = parseISO(session.created_at);
          let dateKey: string;
          if (aggregationType === 'monthly') {
            dateKey = format(d, 'yyyy-MM');
          } else if (aggregationType === 'weekly') {
            // Key by the Monday of the week
            dateKey = format(startOfWeek(d, { weekStartsOn: 1 }), 'yyyy-MM-dd');
          } else {
            dateKey = format(d, 'yyyy-MM-dd');
          }
          aggregatedRevenue.set(dateKey, (aggregatedRevenue.get(dateKey) || 0) + Number(session.amount));
        } catch (error) {
          console.error('Error processing revenue data:', error);
        }
      });

      const formattedData = Array.from(aggregatedRevenue.entries())
        .map(([date, revenue]) => ({
          date,
          revenue: Number(revenue.toFixed(2)),
          aggregationType,
        }))
        .sort((a, b) => new Date(a.date + (aggregationType === 'monthly' ? '-01' : '')).getTime() - new Date(b.date + (aggregationType === 'monthly' ? '-01' : '')).getTime())
        .map(item => ({
          ...item,
          displayDate: item.aggregationType === 'monthly'
            ? format(parseISO(item.date + '-01'), 'MMM yyyy')   // "Jan 2024"
            : item.aggregationType === 'weekly'
              ? format(parseISO(item.date), 'MMM d')             // "Jan 6" (week start)
              : format(parseISO(item.date), 'MMM d'),            // "Jan 15"
          originalDate: item.date,
        }));

      setRevenueData(formattedData);
    } catch (error) {
      toast.error('Failed to fetch revenue data');
    }
  };

  const fetchStats = useCallback(async () => {
    try {
      const now = new Date();
      const todayStart = startOfDay(now);
      const todayEnd = endOfDay(now);
      const monthStart = startOfMonth(now);
      const monthEnd = endOfMonth(now);
      const lastMonthStart = startOfMonth(subDays(monthStart, 1));
      const lastMonthEnd = endOfMonth(subDays(monthStart, 1));
      const nextHour = addHours(now, 1);

      // Create fresh query instances for each stat to avoid conflicts
      
      // For active parkers - query both lot_id matches AND null lot_id with matching location
      let activeParkers: { id: string }[] = [];
      if (lot) {
        const { data: activeWithLotId } = await supabase.from('parking_sessions').select('id')
          .eq('payment_status', 'paid')
          .gte('expires_at', now.toISOString())
          .eq('lot_id', lot.id);
        
        const { data: activeNullLotId } = await supabase.from('parking_sessions').select('id')
          .eq('payment_status', 'paid')
          .gte('expires_at', now.toISOString())
          .is('lot_id', null)
          .eq('location', lot.display_name);
        
        activeParkers = [...(activeWithLotId || []), ...(activeNullLotId || [])];
      } else {
        const { data } = await supabase.from('parking_sessions').select('id')
          .eq('payment_status', 'paid')
          .gte('expires_at', now.toISOString());
        activeParkers = data || [];
      }

      // For today's revenue - query both lot_id matches AND null lot_id with matching location
      let todayRevenue: { amount: number }[] = [];
      if (lot) {
        // Sessions with correct lot_id
        const { data: todayWithLotId } = await supabase.from('parking_sessions').select('amount')
          .eq('payment_status', 'paid')
          .gte('created_at', todayStart.toISOString())
          .lte('created_at', todayEnd.toISOString())
          .eq('lot_id', lot.id);
        
        // Sessions with null lot_id but matching location (legacy data)
        const { data: todayNullLotId } = await supabase.from('parking_sessions').select('amount')
          .eq('payment_status', 'paid')
          .gte('created_at', todayStart.toISOString())
          .lte('created_at', todayEnd.toISOString())
          .is('lot_id', null)
          .eq('location', lot.display_name);
        
        todayRevenue = [...(todayWithLotId || []), ...(todayNullLotId || [])];
      } else {
        const { data } = await supabase.from('parking_sessions').select('amount')
          .eq('payment_status', 'paid')
          .gte('created_at', todayStart.toISOString())
          .lte('created_at', todayEnd.toISOString());
        todayRevenue = data || [];
      }

      // For this month's revenue - same pattern
      let monthlyRevenue: { amount: number }[] = [];
      if (lot) {
        const { data: monthlyWithLotId } = await supabase.from('parking_sessions').select('amount')
          .eq('payment_status', 'paid')
          .gte('created_at', monthStart.toISOString())
          .lte('created_at', monthEnd.toISOString())
          .eq('lot_id', lot.id);
        
        const { data: monthlyNullLotId } = await supabase.from('parking_sessions').select('amount')
          .eq('payment_status', 'paid')
          .gte('created_at', monthStart.toISOString())
          .lte('created_at', monthEnd.toISOString())
          .is('lot_id', null)
          .eq('location', lot.display_name);
        
        monthlyRevenue = [...(monthlyWithLotId || []), ...(monthlyNullLotId || [])];
      } else {
        const { data } = await supabase.from('parking_sessions').select('amount')
          .eq('payment_status', 'paid')
          .gte('created_at', monthStart.toISOString())
          .lte('created_at', monthEnd.toISOString());
        monthlyRevenue = data || [];
      }

      // For last month's revenue - same pattern
      let lastMonthRevenue: { amount: number }[] = [];
      if (lot) {
        const { data: lastMonthWithLotId } = await supabase.from('parking_sessions').select('amount')
          .eq('payment_status', 'paid')
          .gte('created_at', lastMonthStart.toISOString())
          .lte('created_at', lastMonthEnd.toISOString())
          .eq('lot_id', lot.id);
        
        const { data: lastMonthNullLotId } = await supabase.from('parking_sessions').select('amount')
          .eq('payment_status', 'paid')
          .gte('created_at', lastMonthStart.toISOString())
          .lte('created_at', lastMonthEnd.toISOString())
          .is('lot_id', null)
          .eq('location', lot.display_name);
        
        lastMonthRevenue = [...(lastMonthWithLotId || []), ...(lastMonthNullLotId || [])];
      } else {
        const { data } = await supabase.from('parking_sessions').select('amount')
          .eq('payment_status', 'paid')
          .gte('created_at', lastMonthStart.toISOString())
          .lte('created_at', lastMonthEnd.toISOString());
        lastMonthRevenue = data || [];
      }

      // For expiring sessions - same pattern for consistency
      let expiringSessions: { id: string }[] = [];
      if (lot) {
        const { data: expiringWithLotId } = await supabase.from('parking_sessions').select('id')
          .or('payment_status.eq.paid,payment_status.eq.Yolo')
          .gte('expires_at', now.toISOString())
          .lte('expires_at', nextHour.toISOString())
          .eq('lot_id', lot.id);
        
        const { data: expiringNullLotId } = await supabase.from('parking_sessions').select('id')
          .or('payment_status.eq.paid,payment_status.eq.Yolo')
          .gte('expires_at', now.toISOString())
          .lte('expires_at', nextHour.toISOString())
          .is('lot_id', null)
          .eq('location', lot.display_name);
        
        expiringSessions = [...(expiringWithLotId || []), ...(expiringNullLotId || [])];
      } else {
        const { data } = await supabase.from('parking_sessions').select('id')
          .or('payment_status.eq.paid,payment_status.eq.Yolo')
          .gte('expires_at', now.toISOString())
          .lte('expires_at', nextHour.toISOString());
        expiringSessions = data || [];
      }

      // Helpers to categorize sessions into three groups based on payment_method_type
      const isFreeParker = (s: any) => s.payment_status === 'Yolo';
      const isPaidSubscription = (s: any) => s.payment_method_type === 'subscription' && !isFreeParker(s);
      const isRegularParking = (s: any) => !isFreeParker(s) && !isPaidSubscription(s);

      // Separate active parkers into three categories
      const allActiveParkers = activeParkers || [];
      
      // For today's revenue, we need the full session data to check subscription status
      // Refetch with full data for proper categorization
      let todaySessionsFull: any[] = [];
      let monthSessionsFull: any[] = [];
      let lastMonthSessionsFull: any[] = [];

      if (lot) {
        const { data: todayWithLotIdFull } = await supabase.from('parking_sessions').select('*')
          .eq('payment_status', 'paid')
          .gte('created_at', todayStart.toISOString())
          .lte('created_at', todayEnd.toISOString())
          .eq('lot_id', lot.id);
        
        const { data: todayNullLotIdFull } = await supabase.from('parking_sessions').select('*')
          .eq('payment_status', 'paid')
          .gte('created_at', todayStart.toISOString())
          .lte('created_at', todayEnd.toISOString())
          .is('lot_id', null)
          .eq('location', lot.display_name);

        // Also get Yolo sessions for subscription stats
        const { data: todayYoloWithLotId } = await supabase.from('parking_sessions').select('*')
          .eq('payment_status', 'Yolo')
          .gte('created_at', todayStart.toISOString())
          .lte('created_at', todayEnd.toISOString())
          .eq('lot_id', lot.id);

        const { data: todayYoloNullLotId } = await supabase.from('parking_sessions').select('*')
          .eq('payment_status', 'Yolo')
          .gte('created_at', todayStart.toISOString())
          .lte('created_at', todayEnd.toISOString())
          .is('lot_id', null)
          .eq('location', lot.display_name);

        todaySessionsFull = [
          ...(todayWithLotIdFull || []), 
          ...(todayNullLotIdFull || []),
          ...(todayYoloWithLotId || []),
          ...(todayYoloNullLotId || [])
        ];

        // Monthly sessions with full data
        const { data: monthWithLotIdFull } = await supabase.from('parking_sessions').select('*')
          .eq('payment_status', 'paid')
          .gte('created_at', monthStart.toISOString())
          .lte('created_at', monthEnd.toISOString())
          .eq('lot_id', lot.id);
        
        const { data: monthNullLotIdFull } = await supabase.from('parking_sessions').select('*')
          .eq('payment_status', 'paid')
          .gte('created_at', monthStart.toISOString())
          .lte('created_at', monthEnd.toISOString())
          .is('lot_id', null)
          .eq('location', lot.display_name);

        const { data: monthYoloWithLotId } = await supabase.from('parking_sessions').select('*')
          .eq('payment_status', 'Yolo')
          .gte('created_at', monthStart.toISOString())
          .lte('created_at', monthEnd.toISOString())
          .eq('lot_id', lot.id);

        const { data: monthYoloNullLotId } = await supabase.from('parking_sessions').select('*')
          .eq('payment_status', 'Yolo')
          .gte('created_at', monthStart.toISOString())
          .lte('created_at', monthEnd.toISOString())
          .is('lot_id', null)
          .eq('location', lot.display_name);

        monthSessionsFull = [
          ...(monthWithLotIdFull || []), 
          ...(monthNullLotIdFull || []),
          ...(monthYoloWithLotId || []),
          ...(monthYoloNullLotId || [])
        ];

        // Last month sessions with full data
        const { data: lastMonthWithLotIdFull } = await supabase.from('parking_sessions').select('*')
          .eq('payment_status', 'paid')
          .gte('created_at', lastMonthStart.toISOString())
          .lte('created_at', lastMonthEnd.toISOString())
          .eq('lot_id', lot.id);
        
        const { data: lastMonthNullLotIdFull } = await supabase.from('parking_sessions').select('*')
          .eq('payment_status', 'paid')
          .gte('created_at', lastMonthStart.toISOString())
          .lte('created_at', lastMonthEnd.toISOString())
          .is('lot_id', null)
          .eq('location', lot.display_name);

        const { data: lastMonthYoloWithLotId } = await supabase.from('parking_sessions').select('*')
          .eq('payment_status', 'Yolo')
          .gte('created_at', lastMonthStart.toISOString())
          .lte('created_at', lastMonthEnd.toISOString())
          .eq('lot_id', lot.id);

        const { data: lastMonthYoloNullLotId } = await supabase.from('parking_sessions').select('*')
          .eq('payment_status', 'Yolo')
          .gte('created_at', lastMonthStart.toISOString())
          .lte('created_at', lastMonthEnd.toISOString())
          .is('lot_id', null)
          .eq('location', lot.display_name);

        lastMonthSessionsFull = [
          ...(lastMonthWithLotIdFull || []), 
          ...(lastMonthNullLotIdFull || []),
          ...(lastMonthYoloWithLotId || []),
          ...(lastMonthYoloNullLotId || [])
        ];

        // Also get active parkers with full data to categorize subscriptions
        const { data: activeWithLotIdFull } = await supabase.from('parking_sessions').select('*')
          .or('payment_status.eq.paid,payment_status.eq.Yolo')
          .gte('expires_at', now.toISOString())
          .or(`subscription_cancelled_at.is.null,subscription_cancelled_at.gt.${now.toISOString()}`)
          .eq('lot_id', lot.id);
        
        const { data: activeNullLotIdFull } = await supabase.from('parking_sessions').select('*')
          .or('payment_status.eq.paid,payment_status.eq.Yolo')
          .gte('expires_at', now.toISOString())
          .or(`subscription_cancelled_at.is.null,subscription_cancelled_at.gt.${now.toISOString()}`)
          .is('lot_id', null)
          .eq('location', lot.display_name);

        const allActiveFull = [...(activeWithLotIdFull || []), ...(activeNullLotIdFull || [])];
        const activeSubscriptions = allActiveFull.filter(isPaidSubscription);
        const activeParkingOnly = allActiveFull.filter(isRegularParking);
        const activeFreeParkers = allActiveFull.filter(isFreeParker);

        // Calculate revenue by category
        const todaySubRevenue = todaySessionsFull.filter(isPaidSubscription).reduce((sum, s) => sum + Number(s.amount), 0);
        const todayParkingRevenue = todaySessionsFull.filter(isRegularParking).reduce((sum, s) => sum + Number(s.amount), 0);

        const monthSubRevenue = monthSessionsFull.filter(isPaidSubscription).reduce((sum, s) => sum + Number(s.amount), 0);
        const monthParkingRevenue = monthSessionsFull.filter(isRegularParking).reduce((sum, s) => sum + Number(s.amount), 0);

        const lastMonthSubRevenue = lastMonthSessionsFull.filter(isPaidSubscription).reduce((sum, s) => sum + Number(s.amount), 0);
        const lastMonthParkingRevenue = lastMonthSessionsFull.filter(isRegularParking).reduce((sum, s) => sum + Number(s.amount), 0);

        setStats({
          activeParkers: allActiveFull.length,
          todayRevenue: todaySessionsFull.reduce((sum, s) => sum + Number(s.amount), 0),
          monthlyRevenue: monthSessionsFull.reduce((sum, s) => sum + Number(s.amount), 0),
          lastMonthRevenue: lastMonthSessionsFull.reduce((sum, s) => sum + Number(s.amount), 0),
          expiringCount: expiringSessions?.length || 0,
          // Paid subscription stats (amount >= $100)
          activeSubscriptions: activeSubscriptions.length,
          subscriptionTodayRevenue: todaySubRevenue,
          subscriptionMonthlyRevenue: monthSubRevenue,
          subscriptionLastMonthRevenue: lastMonthSubRevenue,
          // Regular parking stats (amount < $100, not Yolo)
          parkingActiveParkers: activeParkingOnly.length,
          parkingTodayRevenue: todayParkingRevenue,
          parkingMonthlyRevenue: monthParkingRevenue,
          parkingLastMonthRevenue: lastMonthParkingRevenue,
          // Free parker stats
          freeParkerCount: allActiveFull.filter(isFreeParker).length,
          activeFreeParkers: activeFreeParkers.length
        });
      } else {
        setStats({
          activeParkers: activeParkers?.length || 0,
          todayRevenue: todayRevenue?.reduce((sum, session) => sum + Number(session.amount), 0) || 0,
          monthlyRevenue: monthlyRevenue?.reduce((sum, session) => sum + Number(session.amount), 0) || 0,
          lastMonthRevenue: lastMonthRevenue?.reduce((sum, session) => sum + Number(session.amount), 0) || 0,
          expiringCount: expiringSessions?.length || 0,
          activeSubscriptions: 0,
          subscriptionTodayRevenue: 0,
          subscriptionMonthlyRevenue: 0,
          subscriptionLastMonthRevenue: 0,
          parkingActiveParkers: activeParkers?.length || 0,
          parkingTodayRevenue: todayRevenue?.reduce((sum, session) => sum + Number(session.amount), 0) || 0,
          parkingMonthlyRevenue: monthlyRevenue?.reduce((sum, session) => sum + Number(session.amount), 0) || 0,
          parkingLastMonthRevenue: lastMonthRevenue?.reduce((sum, session) => sum + Number(session.amount), 0) || 0,
          freeParkerCount: 0,
          activeFreeParkers: 0
        });
      }
    } catch (error) {
      toast.error('Failed to fetch statistics');
    }
  }, [lot]);

  // Populate stats (including last month's revenue) on initial load
  useEffect(() => {
    if (!isLoading && lot && userRole !== 'none') {
      fetchStats();
    }
  }, [isLoading, lot, userRole, fetchStats]);

  const fetchSessions = useCallback(async (page = 1, pageSize = serverPagination.pageSize, showLoader = false) => {
    try {
      if (showLoader) {
        setLoading(true);
      }
      
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      
      // Helper to apply view-specific filters
      const applyViewFilters = (q: any) => {
        const isAdminOrOwner = userRole === 'admin' || userRole === 'owner';
        if (isAdminOrOwner && currentView === 'free_parkers') {
          return q.eq('payment_status', 'Yolo');
        } else if (isAdminOrOwner && currentView === 'subscriptions') {
          return q.eq('payment_method_type', 'subscription');
        } else if (isAdminOrOwner && currentView === 'parking') {
          return q.neq('payment_method_type', 'subscription').neq('payment_status', 'Yolo');
        }
        return q;
      };

      // Get total count (only when needed - first load or page 1)
      if (serverPagination.totalRecords === 0 || page === 1) {
        let countQuery = supabase
          .from('parking_sessions')
          .select('*', { count: 'exact', head: true });
        
        if (lot) {
          countQuery = countQuery.filter('lot_id', 'eq', lot.id);
        }
        
        countQuery = applyViewFilters(countQuery);
        countQuery = applyStatusFilters(countQuery);
        
        const { count } = await countQuery;
        console.log('Total records in lot:', count);
        
        setServerPagination(prev => ({
          ...prev,
          totalRecords: count || 0,
          totalPages: Math.ceil((count || 0) / pageSize)
        }));
        setTotalSessions(count || 0);
      }
      
      // Fetch paginated data
      let query = supabase
        .from('parking_sessions')
        .select('*')
        .order(sortField, { ascending: sortDirection === 'asc' })
        .range(from, to);
      
      if (lot) {
        query = query.filter('lot_id', 'eq', lot.id);
      }
      
      query = applyViewFilters(query);
      query = applyStatusFilters(query);

      const { data, error } = await query;

      if (error) throw error;
      
      setSessions(data || []);
      setServerPagination(prev => ({ ...prev, currentPage: page }));
      
    } catch (error) {
      toast.error('Failed to fetch parking sessions');
    } finally {
      if (showLoader) {
        setLoading(false);
      }
    }
  }, [lot, sortField, sortDirection, serverPagination.pageSize, serverPagination.totalRecords, applyStatusFilters, currentView, userRole]);

  const searchSessions = useCallback(async (searchTerm: string, page = 1) => {
    if (!searchTerm.trim()) {
      return fetchSessions(page, serverPagination.pageSize);
    }
    
    try {
      setIsSearching(true);
      
      const from = (page - 1) * serverPagination.pageSize;
      const to = from + serverPagination.pageSize - 1;
      
      // Search with server-side filtering - improved for space-insensitive and fuzzy matching
      // Remove all whitespace from search term for whitespace-insensitive license plate search
      const cleanSearchTerm = searchTerm.replace(/\s+/g, '');
      
      // License plate search - multiple patterns to handle whitespace variations
      // Since Supabase doesn't support complex SQL functions in URL params, use multiple simple conditions
      const searchConditions = [
        `license_plate.ilike.%${cleanSearchTerm}%`,
        `license_plate.ilike.%${cleanSearchTerm.split('').join(' ')}%`,
        `license_plate.ilike.%${cleanSearchTerm.split('').join('  ')}%`
      ];
      
      // Add common license plate patterns if the term has letters and numbers
      if (/[A-Za-z]/.test(cleanSearchTerm) && /[0-9]/.test(cleanSearchTerm)) {
        // Try adding space between letters and numbers (e.g., CMED468 -> CMED 468)
        const spaceAddedTerm = cleanSearchTerm.replace(/([A-Za-z]+)([0-9]+)/g, '$1 $2');
        if (spaceAddedTerm !== cleanSearchTerm) {
          searchConditions.push(`license_plate.ilike.%${spaceAddedTerm}%`);
        }
      }
      
      let query = supabase
        .from('parking_sessions')
        .select('*', { count: 'exact' })
        .order(sortField, { ascending: sortDirection === 'asc' })
        .range(from, to)
        .or(searchConditions.join(','));
      
      if (lot) {
        query = query.filter('lot_id', 'eq', lot.id);
      }
      
      // Apply view-specific filters for admin/owner
      const isAdminOrOwner = userRole === 'admin' || userRole === 'owner';
      if (isAdminOrOwner && currentView === 'free_parkers') {
        query = query.eq('payment_status', 'Yolo');
      } else if (isAdminOrOwner && currentView === 'subscriptions') {
        query = query.eq('payment_method_type', 'subscription');
      } else if (isAdminOrOwner && currentView === 'parking') {
        query = query.neq('payment_method_type', 'subscription').neq('payment_status', 'Yolo');
      }
      
      query = applyStatusFilters(query);
      
      const { data, error, count } = await query;
      
      if (error) throw error;
      
      setSessions(data || []);
      setServerPagination(prev => ({
        ...prev,
        currentPage: page,
        totalRecords: count || 0,
        totalPages: Math.ceil((count || 0) / prev.pageSize)
      }));
      setTotalSessions(count || 0);
      
    } catch (error) {
      toast.error('Failed to search sessions');
    } finally {
      setIsSearching(false);
    }
  }, [lot, sortField, sortDirection, serverPagination.pageSize, applyStatusFilters, currentView, userRole]);

  const sortSessions = useCallback(async () => {
    try {
      setIsSorting(true);
      
      if (searchQuery.trim()) {
        await searchSessions(searchQuery.trim(), 1);
      } else {
        await fetchSessions(1, serverPagination.pageSize);
      }
    } catch (error) {
      toast.error('Failed to sort sessions');
    } finally {
      setIsSorting(false);
    }
  }, [searchQuery, searchSessions, fetchSessions, serverPagination.pageSize]);

  const handleSort = (field: string) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getSortIcon = (field: string) => {
    if (field !== sortField) return null;
    
    if (isSorting) {
      return <Loader2 className="inline ml-1 animate-spin text-blue-500" size={14} />;
    }
    
    return sortDirection === 'asc' ? (
      <ChevronUp size={16} className="inline" />
    ) : (
      <ChevronDown size={16} className="inline" />
    );
  };

  const isExpired = (expiresAt: string | null): boolean => {
    if (!expiresAt) return true;
    try {
      return parseISO(expiresAt) < new Date();
    } catch (error) {
      console.error('Error checking expiry:', error);
      return true;
    }
  };

  const getSubscriptionStatus = (session: any): 'Scheduled' | 'Active' | 'Cancelled' | 'Expired' => {
    if (session.scheduled_start_date) {
      try {
        if (parseISO(session.scheduled_start_date) > new Date()) {
          return 'Scheduled';
        }
      } catch {}
    }
    if (isExpired(session.expires_at)) return 'Expired';
    if (session.subscription_cancelled_at) return 'Cancelled';
    return 'Active';
  };

  const handlePageChange = useCallback(async (newPage: number) => {
    try {
      setIsPaginating(true);
      
      if (searchQuery) {
        await searchSessions(searchQuery, newPage);
      } else {
        await fetchSessions(newPage, serverPagination.pageSize);
      }
    } catch (error) {
      toast.error('Failed to change page');
    } finally {
      setIsPaginating(false);
    }
  }, [searchQuery, searchSessions, fetchSessions, serverPagination.pageSize]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/admin');
  };

  // Show loading state if lot data is still loading
  if (isLoading) {
    return (
      <div className="min-h-screen bg-cream-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-confetti-pink mx-auto mb-4" />
          <p className="text-ink-500">Loading parking lot...</p>
        </div>
      </div>
    );
  }

  // Show error state if lot data failed to load
  if (error || !lot || !settings) {
    return (
      <div className="min-h-screen bg-cream-100 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-full max-w-md bg-white rounded-xl shadow-lg p-6">
            <MapPin className="h-8 w-8 text-red-600 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-ink-900 mb-2">
              Unable to Load Parking Lot
            </h2>
            <p className="text-ink-500 mb-4">
              {error || 'Lot information not available'}
            </p>
            <Link
              to="/admin"
              className="inline-flex items-center px-4 py-2 bg-confetti-pink text-white rounded-lg hover:bg-confetti-pink/90 transition-colors"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Admin
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Check user permissions
  if (userRole === 'none') {
    return (
      <div className="min-h-screen bg-cream-100 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-full max-w-md bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-semibold text-ink-900 mb-2">
              Access Denied
            </h2>
            <p className="text-ink-500 mb-4">
              You don't have permission to access this parking lot dashboard.
            </p>
            <Link
              to="/admin"
              className="inline-flex items-center px-4 py-2 bg-confetti-pink text-white rounded-lg hover:bg-confetti-pink/90 transition-colors"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Admin
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-cream-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-confetti-pink"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream-100">
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Logo size={24} />
            
            {/* Lot Switcher Dropdown */}
            {accessibleLots.length > 1 && (
              <div className="relative ml-4" ref={dropdownRef}>
                <button
                  onClick={() => setShowLotDropdown(!showLotDropdown)}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  disabled={loadingLots}
                >
                  <MapPin size={16} />
                  <span className="truncate max-w-32">
                    {lot?.display_name || 'Select Lot'}
                  </span>
                  {loadingLots ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <ChevronDown size={16} className={`transition-transform ${showLotDropdown ? 'rotate-180' : ''}`} />
                  )}
                </button>

                {showLotDropdown && (
                  <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-300 rounded-md shadow-lg z-50">
                    <div className="py-1">
                      <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200">
                        Switch Parking Lot
                      </div>
                      {accessibleLots.map((accessibleLot) => (
                        <button
                          key={accessibleLot.lot_id}
                          onClick={() => handleLotSwitch(accessibleLot.lot_slug)}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 flex items-center justify-between ${
                            accessibleLot.lot_slug === lot?.slug 
                              ? 'bg-indigo-50 text-indigo-700 font-medium' 
                              : 'text-gray-700'
                          }`}
                        >
                          <div>
                            <div className="font-medium">{accessibleLot.lot_display_name}</div>
                            <div className="text-xs text-gray-500 capitalize">{accessibleLot.role}</div>
                          </div>
                          {accessibleLot.lot_slug === lot?.slug && (
                            <div className="w-2 h-2 bg-indigo-600 rounded-full"></div>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => {
                fetchSessions(1, serverPagination.pageSize, true);
                fetchStats();
                fetchRevenueData();
                toast.success('Dashboard refreshed');
              }}
              className="text-gray-500 hover:text-gray-700"
              title="Refresh Dashboard"
            >
              <RefreshCw size={20} />
            </button>
            {(userRole === 'admin' || userRole === 'owner') && (
              <>
                <Link
                  to={`/admin/lots/${lot?.slug}/users`}
                  className="text-gray-500 hover:text-gray-700"
                  title="Manage Users"
                >
                  <Users size={20} />
                </Link>
                <Link
                  to={`/admin/lots/${lot?.slug}/settings`}
                  className="text-gray-500 hover:text-gray-700"
                  title="Settings"
                >
                  <Settings size={20} />
                </Link>
              </>
            )}
            <button
              onClick={handleLogout}
              className="text-gray-500 hover:text-gray-700"
              title="Logout"
            >
              <LogOut size={20} />
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* View Toggle Tabs - only for admin/owner */}
        {(userRole === 'admin' || userRole === 'owner') && (
          <div className="mb-6">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                <button
                  onClick={() => setCurrentView('all')}
                  className={`group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                    currentView === 'all'
                      ? 'border-gray-700 text-gray-900'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Users className={`mr-2 h-5 w-5 ${currentView === 'all' ? 'text-gray-700' : 'text-gray-400 group-hover:text-gray-500'}`} />
                  All
                  <span className={`ml-2 py-0.5 px-2.5 rounded-full text-xs font-medium ${
                    currentView === 'all' ? 'bg-gray-200 text-gray-800' : 'bg-gray-100 text-gray-900'
                  }`}>
                    {stats.activeParkers}
                  </span>
                </button>
                <button
                  onClick={() => setCurrentView('parking')}
                  className={`group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                    currentView === 'parking'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Car className={`mr-2 h-5 w-5 ${currentView === 'parking' ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-500'}`} />
                  Parking Sessions
                  <span className={`ml-2 py-0.5 px-2.5 rounded-full text-xs font-medium ${
                    currentView === 'parking' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-900'
                  }`}>
                    {stats.parkingActiveParkers}
                  </span>
                </button>
                <button
                  onClick={() => setCurrentView('subscriptions')}
                  className={`group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                    currentView === 'subscriptions'
                      ? 'border-purple-500 text-purple-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Repeat className={`mr-2 h-5 w-5 ${currentView === 'subscriptions' ? 'text-purple-500' : 'text-gray-400 group-hover:text-gray-500'}`} />
                  Subscriptions
                  <span className={`ml-2 py-0.5 px-2.5 rounded-full text-xs font-medium ${
                    currentView === 'subscriptions' ? 'bg-purple-100 text-purple-600' : 'bg-gray-100 text-gray-900'
                  }`}>
                    {stats.activeSubscriptions}
                  </span>
                </button>
                <button
                  onClick={() => setCurrentView('free_parkers')}
                  className={`group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                    currentView === 'free_parkers'
                      ? 'border-green-500 text-green-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <UserCheck className={`mr-2 h-5 w-5 ${currentView === 'free_parkers' ? 'text-green-500' : 'text-gray-400 group-hover:text-gray-500'}`} />
                  Free Parkers
                  <span className={`ml-2 py-0.5 px-2.5 rounded-full text-xs font-medium ${
                    currentView === 'free_parkers' ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-900'
                  }`}>
                    {stats.activeFreeParkers}
                  </span>
                </button>
              </nav>
            </div>
          </div>
        )}

        {(userRole === 'admin' || userRole === 'owner') && (
          <>
            <div className={`grid grid-cols-1 gap-6 mb-8 ${currentView === 'free_parkers' ? 'md:grid-cols-2' : 'md:grid-cols-2 lg:grid-cols-4'}`}>
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <div className="flex items-center">
                    <div className={`flex-shrink-0 rounded-md p-3 ${
                      currentView === 'subscriptions' ? 'bg-purple-100' : currentView === 'free_parkers' ? 'bg-green-100' : currentView === 'all' ? 'bg-gray-100' : 'bg-blue-100'
                    }`}>
                      {currentView === 'subscriptions' ? (
                        <Repeat className="h-6 w-6 text-purple-600" aria-hidden="true" />
                      ) : currentView === 'free_parkers' ? (
                        <UserCheck className="h-6 w-6 text-green-600" aria-hidden="true" />
                      ) : currentView === 'all' ? (
                        <Users className="h-6 w-6 text-gray-600" aria-hidden="true" />
                      ) : (
                        <Users className="h-6 w-6 text-blue-600" aria-hidden="true" />
                      )}
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          {currentView === 'subscriptions' ? 'Active Subscriptions' : currentView === 'free_parkers' ? 'Active Free Parkers' : currentView === 'all' ? 'Total Active Parkers' : 'Active Parkers'}
                        </dt>
                        <dd className="flex items-baseline">
                          <div className="text-2xl font-semibold text-gray-900">
                            {currentView === 'subscriptions' ? stats.activeSubscriptions : currentView === 'free_parkers' ? stats.activeFreeParkers : currentView === 'all' ? stats.activeParkers : stats.parkingActiveParkers}
                          </div>
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              {currentView === 'free_parkers' ? (
                /* Free Parkers view: show expiring count instead of revenue */
                <div className="bg-white overflow-hidden shadow rounded-lg">
                  <div className="px-4 py-5 sm:p-6">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 bg-yellow-100 rounded-md p-3">
                        <Clock className="h-6 w-6 text-yellow-600" aria-hidden="true" />
                      </div>
                      <div className="ml-5 w-0 flex-1">
                        <dl>
                          <dt className="text-sm font-medium text-gray-500 truncate">Expiring Within 1 Hour</dt>
                          <dd className="flex items-baseline">
                            <div className="text-2xl font-semibold text-gray-900">{stats.expiringCount}</div>
                          </dd>
                        </dl>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                /* Parking / Subscriptions view: show today's revenue */
                <div className="bg-white overflow-hidden shadow rounded-lg">
                  <div className="px-4 py-5 sm:p-6">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 bg-green-100 rounded-md p-3">
                        <DollarSign className="h-6 w-6 text-green-600" aria-hidden="true" />
                      </div>
                      <div className="ml-5 w-0 flex-1">
                        <dl>
                          <dt className="text-sm font-medium text-gray-500 truncate">
                            Today's {currentView === 'subscriptions' ? 'Subscription' : currentView === 'all' ? 'Total' : 'Parking'} Revenue
                          </dt>
                          <dd className="flex items-baseline">
                            <div className="text-2xl font-semibold text-gray-900">
                              ${(currentView === 'subscriptions' ? stats.subscriptionTodayRevenue : currentView === 'all' ? stats.todayRevenue : stats.parkingTodayRevenue).toFixed(2)}
                            </div>
                          </dd>
                        </dl>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {currentView !== 'free_parkers' && (
                <div className="bg-white overflow-hidden shadow rounded-lg">
                  <div className="px-4 py-5 sm:p-6">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 bg-purple-100 rounded-md p-3">
                        <DollarSign className="h-6 w-6 text-purple-600" aria-hidden="true" />
                      </div>
                      <div className="ml-5 w-0 flex-1">
                        <dl>
                          <dt className="text-sm font-medium text-gray-500 truncate">
                            This Month's {currentView === 'subscriptions' ? 'Subscription' : currentView === 'all' ? 'Total' : 'Parking'} Revenue
                          </dt>
                          <dd className="flex items-baseline">
                            <div className="text-2xl font-semibold text-gray-900">
                              ${(currentView === 'subscriptions' ? stats.subscriptionMonthlyRevenue : currentView === 'all' ? stats.monthlyRevenue : stats.parkingMonthlyRevenue).toFixed(2)}
                            </div>
                            {(currentView === 'subscriptions' ? stats.subscriptionLastMonthRevenue : currentView === 'all' ? stats.lastMonthRevenue : stats.parkingLastMonthRevenue) > 0 && (
                              <span
                                className={`ml-2 flex items-baseline text-sm font-semibold ${
                                  (currentView === 'subscriptions' ? stats.subscriptionMonthlyRevenue : currentView === 'all' ? stats.monthlyRevenue : stats.parkingMonthlyRevenue) >= 
                                  (currentView === 'subscriptions' ? stats.subscriptionLastMonthRevenue : currentView === 'all' ? stats.lastMonthRevenue : stats.parkingLastMonthRevenue)
                                    ? 'text-green-600'
                                    : 'text-red-600'
                                }`}
                              >
                                {calculatePercentageChange(
                                  currentView === 'subscriptions' ? stats.subscriptionMonthlyRevenue : currentView === 'all' ? stats.monthlyRevenue : stats.parkingMonthlyRevenue,
                                  currentView === 'subscriptions' ? stats.subscriptionLastMonthRevenue : currentView === 'all' ? stats.lastMonthRevenue : stats.parkingLastMonthRevenue
                                ).toFixed(0)}%
                              </span>
                            )}
                          </dd>
                        </dl>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {currentView !== 'free_parkers' && (
                <div className="bg-white overflow-hidden shadow rounded-lg">
                  <div className="px-4 py-5 sm:p-6">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 bg-indigo-100 rounded-md p-3">
                        <DollarSign className="h-6 w-6 text-indigo-600" aria-hidden="true" />
                      </div>
                      <div className="ml-5 w-0 flex-1">
                        <dl>
                          <dt className="text-sm font-medium text-gray-500 truncate">
                            Last Month's {currentView === 'subscriptions' ? 'Subscription' : currentView === 'all' ? 'Total' : 'Parking'} Revenue
                          </dt>
                          <dd>
                            <div className="text-2xl font-semibold text-gray-900">
                              ${(currentView === 'subscriptions' ? stats.subscriptionLastMonthRevenue : currentView === 'all' ? stats.lastMonthRevenue : stats.parkingLastMonthRevenue).toFixed(2)}
                            </div>
                          </dd>
                        </dl>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {currentView !== 'free_parkers' && (
            <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8">
              <div className="px-4 py-5 sm:px-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900">Revenue Overview</h3>
                <div className="mt-2 flex items-center justify-between">
                  <p className="text-sm text-gray-500">
                    View revenue trends over time {timeRange === '365' ? '(monthly aggregation)' : timeRange === '30' ? '(weekly aggregation)' : '(daily aggregation)'}
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setTimeRange('7')}
                      className={`px-2 py-1 text-xs rounded-md ${
                        timeRange === '7'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                      }`}
                    >
                      Week
                    </button>
                    <button
                      type="button"
                      onClick={() => setTimeRange('30')}
                      className={`px-2 py-1 text-xs rounded-md ${
                        timeRange === '30'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                      }`}
                    >
                      Month
                    </button>
                    <button
                      type="button"
                      onClick={() => setTimeRange('365')}
                      className={`px-2 py-1 text-xs rounded-md ${
                        timeRange === '365'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                      }`}
                    >
                      Year
                    </button>
                  </div>
                </div>
              </div>
              <div className="px-4 py-5 h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={revenueData}
                    margin={{
                      top: 10,
                      right: 30,
                      left: 20,
                      bottom: 30,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="displayDate"
                      tickMargin={10}
                    />
                    <YAxis
                      tickFormatter={(value) => `$${value}`}
                    />
                    <Tooltip
                      formatter={(value) => [`$${Number(value).toFixed(2)}`, 'Revenue']}
                      labelFormatter={(label, payload) => {
                        try {
                          if (payload && payload.length > 0) {
                            const dataPoint = payload[0].payload;
                            const originalDate = dataPoint.originalDate;
                            const type = dataPoint.aggregationType;
                            
                            if (type === 'monthly') {
                              return format(parseISO(originalDate + '-01'), 'MMMM yyyy');
                            } else if (type === 'weekly') {
                              const weekStart = parseISO(originalDate);
                              const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
                              return `${format(weekStart, 'MMM d')} – ${format(weekEnd, 'MMM d, yyyy')}`;
                            } else {
                              return format(parseISO(originalDate), 'MMMM d, yyyy');
                            }
                          }
                          return label;
                        } catch (error) {
                          return label;
                        }
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="revenue"
                      stroke="#3b82f6"
                      activeDot={{ r: 8 }}
                      strokeWidth={2}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
            )}
          </>
        )}

        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6 flex flex-wrap items-center justify-between gap-4">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              {(userRole === 'admin' || userRole === 'owner') 
                ? currentView === 'subscriptions' 
                  ? 'All Subscriptions' 
                  : currentView === 'free_parkers'
                    ? 'All Free Parkers'
                    : currentView === 'all'
                      ? 'All Sessions'
                      : 'All Parking Sessions'
                : 'All Parking Sessions'}
            </h3>
            <div className="flex items-center gap-4">
              <div className="relative">
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => handleSearchInputChange(e.target.value)}
                  placeholder={(userRole === 'admin' || userRole === 'owner') 
                    ? currentView === 'subscriptions' 
                      ? "Search subscriptions by plate..." 
                      : currentView === 'free_parkers'
                        ? "Search free parkers by plate..."
                        : "Search by plate, phone, or status (smart matching)..."
                    : "Search by plate, phone, or status (smart matching)..."}

                  className={`w-64 rounded-lg border border-gray-300 px-4 py-2 pl-10 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-opacity ${
                    isSearching ? 'opacity-70' : 'opacity-100'
                  }`}
                />
                {isSearching ? (
                  <Loader2
                    size={18}
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-500 animate-spin"
                  />
                ) : (
                  <Search
                    size={18}
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  />
                )}
                {searchQuery && !isSearching && (
                  <button
                    onClick={() => handleSearchInputChange('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    title="Clear search"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
              {(userRole === 'admin' || userRole === 'owner') && currentView === 'free_parkers' && (
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-green-700 transition duration-200"
                >
                  <Plus size={16} />
                  <span>Add Free Parker</span>
                </button>
              )}
            </div>
          </div>

          {/* Status Filters */}
          <div className="px-6 py-4 border-b border-gray-200 relative">
            {isFiltering && (
              <div className="absolute inset-0 bg-white bg-opacity-50 flex items-center justify-center z-10">
                <div className="flex items-center gap-2 text-blue-600">
                  <Loader2 className="animate-spin" size={16} />
                  <span className="text-xs font-medium">Filtering...</span>
                </div>
              </div>
            )}
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-700">Filter by status:</span>
              <div className="flex gap-2">
                <button
                  onClick={() => handleStatusFilterToggle('active')}
                  disabled={isFiltering}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 disabled:cursor-not-allowed ${
                    statusFilters.has('active')
                      ? 'bg-green-100 text-green-800 border-2 border-green-300 shadow-sm'
                      : 'bg-gray-100 text-gray-600 border-2 border-transparent hover:bg-gray-200 hover:border-gray-300'
                  } ${isFiltering ? 'opacity-60' : ''}`}
                >
                  {isFiltering ? (
                    <Loader2 className="inline mr-1 animate-spin" size={14} />
                  ) : (
                    '✅ '
                  )}
                  Active
                  {statusFilters.has('active') && !isFiltering && (
                    <span className="ml-1 text-green-600">×</span>
                  )}
                </button>
                <button
                  onClick={() => handleStatusFilterToggle('expired')}
                  disabled={isFiltering}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 disabled:cursor-not-allowed ${
                    statusFilters.has('expired')
                      ? 'bg-red-100 text-red-800 border-2 border-red-300 shadow-sm'
                      : 'bg-gray-100 text-gray-600 border-2 border-transparent hover:bg-gray-200 hover:border-gray-300'
                  } ${isFiltering ? 'opacity-60' : ''}`}
                >
                  {isFiltering ? (
                    <Loader2 className="inline mr-1 animate-spin" size={14} />
                  ) : (
                    '⏰ '
                  )}
                  Expired
                  {statusFilters.has('expired') && !isFiltering && (
                    <span className="ml-1 text-red-600">×</span>
                  )}
                </button>
                {currentView === 'subscriptions' && (
                  <button
                    onClick={() => handleStatusFilterToggle('scheduled')}
                    disabled={isFiltering}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 disabled:cursor-not-allowed ${
                      statusFilters.has('scheduled')
                        ? 'bg-blue-100 text-blue-800 border-2 border-blue-300 shadow-sm'
                        : 'bg-gray-100 text-gray-600 border-2 border-transparent hover:bg-gray-200 hover:border-gray-300'
                    } ${isFiltering ? 'opacity-60' : ''}`}
                  >
                    {isFiltering ? (
                      <Loader2 className="inline mr-1 animate-spin" size={14} />
                    ) : (
                      '\u{1F4C5} '
                    )}
                    Scheduled
                    {statusFilters.has('scheduled') && !isFiltering && (
                      <span className="ml-1 text-blue-600">×</span>
                    )}
                  </button>
                )}
                {currentView === 'subscriptions' && (
                  <button
                    onClick={() => handleStatusFilterToggle('cancelled')}
                    disabled={isFiltering}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 disabled:cursor-not-allowed ${
                      statusFilters.has('cancelled')
                        ? 'bg-orange-100 text-orange-800 border-2 border-orange-300 shadow-sm'
                        : 'bg-gray-100 text-gray-600 border-2 border-transparent hover:bg-gray-200 hover:border-gray-300'
                    } ${isFiltering ? 'opacity-60' : ''}`}
                  >
                    {isFiltering ? (
                      <Loader2 className="inline mr-1 animate-spin" size={14} />
                    ) : (
                      '🚫 '
                    )}
                    Cancelled
                    {statusFilters.has('cancelled') && !isFiltering && (
                      <span className="ml-1 text-orange-600">×</span>
                    )}
                  </button>
                )}
                {statusFilters.size > 0 && (
                  <button
                    onClick={handleClearAllFilters}
                    disabled={isFiltering}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-all duration-200 disabled:cursor-not-allowed ${
                      isFiltering ? 'opacity-60' : ''
                    }`}
                    title="Clear all filters"
                  >
                    {isFiltering ? (
                      <>
                        <Loader2 className="inline mr-1 animate-spin" size={12} />
                        Clearing...
                      </>
                    ) : (
                      'Clear all'
                    )}
                  </button>
                )}
              </div>
              {statusFilters.size > 0 && (
                <span className="text-xs text-gray-500 ml-2">
                  ({statusFilters.size} filter{statusFilters.size !== 1 ? 's' : ''} applied)
                </span>
              )}
            </div>
          </div>

          <div className="overflow-x-auto relative">
            {isSorting && (
              <div className="absolute inset-0 bg-white bg-opacity-70 flex items-center justify-center z-10">
                <div className="flex items-center gap-2 text-blue-600">
                  <Loader2 className="animate-spin" size={20} />
                  <span className="text-sm font-medium">Sorting...</span>
                </div>
              </div>
            )}
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-opacity ${
                      isSorting ? 'opacity-60 cursor-wait' : ''
                    }`}
                    onClick={() => !isSorting && handleSort('license_plate')}
                  >
                    License Plate {getSortIcon('license_plate')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-opacity ${
                      isSorting ? 'opacity-60 cursor-wait' : ''
                    }`}
                    onClick={() => !isSorting && handleSort('created_at')}
                  >
                    Created {getSortIcon('created_at')}
                  </th>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-opacity ${
                      isSorting ? 'opacity-60 cursor-wait' : ''
                    }`}
                    onClick={() => !isSorting && handleSort('expires_at')}
                  >
                    Expires {getSortIcon('expires_at')}
                  </th>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-opacity ${
                      isSorting ? 'opacity-60 cursor-wait' : ''
                    }`}
                    onClick={() => !isSorting && handleSort('location')}
                  >
                    Location {getSortIcon('location')}
                  </th>
                  {(userRole === 'admin' || userRole === 'owner') && (
                    <>
                      <th
                        className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-opacity ${
                          isSorting ? 'opacity-60 cursor-wait' : ''
                        }`}
                        onClick={() => !isSorting && handleSort('phone')}
                      >
                        Phone {getSortIcon('phone')}
                      </th>
                      {currentView === 'subscriptions' && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Email
                        </th>
                      )}
                      <th
                        className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-opacity ${
                          isSorting ? 'opacity-60 cursor-wait' : ''
                        }`}
                        onClick={() => !isSorting && handleSort('amount')}
                      >
                        Amount {getSortIcon('amount')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Session Type
                      </th>
                    </>
                  )}
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-opacity ${
                      isSorting ? 'opacity-60 cursor-wait' : ''
                    }`}
                    onClick={() => !isSorting && handleSort('payment_status')}
                  >
                    Payment Status {getSortIcon('payment_status')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Notes
                  </th>
                  {(userRole === 'admin' || userRole === 'owner') && (
                    <>
                      <th
                        className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-opacity ${
                          isSorting ? 'opacity-60 cursor-wait' : ''
                        }`}
                        onClick={() => !isSorting && handleSort('transaction_id')}
                      >
                        Transaction ID {getSortIcon('transaction_id')}
                      </th>
                      <th
                        className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-opacity ${
                          isSorting ? 'opacity-60 cursor-wait' : ''
                        }`}
                        onClick={() => !isSorting && handleSort('payment_method_type')}
                      >
                        Payment Details {getSortIcon('payment_method_type')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sessions.map((session) => (
                  <tr key={session.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {session.license_plate}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {(() => {
                        const rawStatus = currentView === 'subscriptions' ? getSubscriptionStatus(session) : (isExpired(session.expires_at) ? 'Expired' : 'Active');
                        const status = (rawStatus === 'Cancelled' && userRole === 'checker') ? 'Active' : rawStatus;
                        const colorClass = status === 'Scheduled'
                          ? 'bg-blue-100 text-blue-800'
                          : status === 'Active'
                            ? 'bg-green-100 text-green-800'
                            : status === 'Cancelled'
                              ? 'bg-orange-100 text-orange-800'
                              : 'bg-red-100 text-red-800';
                        return (
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${colorClass}`}>
                            {status}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(session.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(session.expires_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center gap-2">
                        <MapPin size={16} className="text-gray-400" />
                        {session.location}
                      </div>
                    </td>
                    {(userRole === 'admin' || userRole === 'owner') && (
                      <>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {session.phone}
                        </td>
                        {currentView === 'subscriptions' && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {session.email || <span className="text-gray-300">—</span>}
                          </td>
                        )}
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          ${session.amount?.toFixed(2) || "0.00"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {(() => {
                            const sType = determineSessionType(session, sessions);
                            const sColor =
                              sType === 'Monthly' ? 'bg-purple-100 text-purple-800'
                              : sType === 'Free'    ? 'bg-green-100 text-green-800'
                              : sType === 'Hourly'  ? 'bg-gray-100 text-gray-800'
                              : sType === 'New'     ? 'bg-blue-100 text-blue-800'
                              : 'bg-orange-100 text-orange-800'; // Extension
                            return (
                              <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${sColor}`}>
                                {sType}
                              </span>
                            );
                          })()}
                        </td>
                      </>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          session.payment_status === 'paid'
                            ? 'bg-green-100 text-green-800'
                            : session.payment_status === 'refunded'
                            ? 'bg-red-100 text-red-800'
                            : session.payment_status === 'Yolo'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        {session.payment_status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {session.notes}
                    </td>
                    {(userRole === 'admin' || userRole === 'owner') && (
                      <>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {session.transaction_id || generateTransactionId()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {session.payment_status === 'Yolo' ? (
                            <div className="flex items-center gap-2">
                              <CreditCard size={16} className="text-gray-400" />
                              <span>Free Parker</span>
                            </div>
                          ) : session.payment_method_type && session.card_last4 ? (
                            <div className="flex items-center gap-2">
                              <CreditCard size={16} className="text-gray-400" />
                              <span className="capitalize">
                                {session.payment_method_type} (**** {session.card_last4})
                              </span>
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex items-center gap-2">
                            {session.payment_status === 'paid' && session.payment_method_type === 'subscription' && !session.subscription_cancelled_at && (
                              <button
                                onClick={() => {
                                  setSessionToCancelSub(session);
                                  setCancelSubMode('end_of_period');
                                  setIsCancelSubModalOpen(true);
                                }}
                                className="text-orange-600 hover:text-orange-900"
                                title="Cancel subscription"
                              >
                                <XCircle size={18} />
                              </button>
                            )}
                            {session.payment_status === 'paid' && (
                              <button
                                onClick={() => {
                                  setSessionToRefund(session);
                                  setIsRefundModalOpen(true);
                                }}
                                className="text-red-600 hover:text-red-900"
                                title="Refund payment"
                              >
                                <RefreshCw size={18} />
                              </button>
                            )}
                            <button
                              onClick={() => {
                                setSessionToEdit(session);
                                setEditLicensePlate(session.license_plate);
                                setEditPhone(session.phone || '');
                                setIsEditModalOpen(true);
                              }}
                              className="text-blue-600 hover:text-blue-900"
                              title="Edit session"
                            >
                              <Pencil size={18} />
                            </button>
                            <button
                              onClick={() => {
                                setSessionToDelete(session);
                                setIsDeleteModalOpen(true);
                              }}
                              className="text-red-600 hover:text-red-900"
                              title="Delete session"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {((serverPagination.currentPage - 1) * serverPagination.pageSize) + 1} to{' '}
                {Math.min(serverPagination.currentPage * serverPagination.pageSize, serverPagination.totalRecords)} of{' '}
                {serverPagination.totalRecords} {(userRole === 'admin' || userRole === 'owner') 
                  ? currentView === 'subscriptions' ? 'subscriptions' : currentView === 'free_parkers' ? 'free parkers' : 'sessions'
                  : 'sessions'}
                {(searchQuery || statusFilters.size > 0) && ` (filtered)`}
              </div>
              
              <div className="flex items-center gap-2">
                {/* First page */}
                              <button
                onClick={() => handlePageChange(1)}
                disabled={serverPagination.currentPage === 1 || isPaginating}
                className={`px-3 py-1 text-sm bg-white border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-opacity ${
                  isPaginating ? 'opacity-60' : ''
                }`}
              >
                First
              </button>
                
                {/* Previous page */}
                <button
                  onClick={() => handlePageChange(serverPagination.currentPage - 1)}
                  disabled={serverPagination.currentPage === 1 || isPaginating}
                  className={`inline-flex items-center px-3 py-1 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity ${
                    isPaginating ? 'opacity-60' : ''
                  }`}
                >
                  <ChevronLeft size={16} className="mr-1" />
                  Previous
                </button>
                
                {/* Page numbers */}
                <div className="flex gap-1">
                  {Array.from({ length: Math.min(5, serverPagination.totalPages) }, (_, i) => {
                    const page = Math.max(1, serverPagination.currentPage - 2) + i;
                    if (page > serverPagination.totalPages) return null;
                    
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        disabled={isPaginating}
                        className={`px-3 py-1 text-sm border rounded transition-opacity disabled:cursor-not-allowed ${
                          page === serverPagination.currentPage
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        } ${isPaginating ? 'opacity-60' : ''}`}
                      >
                        {page}
                      </button>
                    );
                  })}
                </div>
                
                {/* Next page */}
                <button
                  onClick={() => handlePageChange(serverPagination.currentPage + 1)}
                  disabled={serverPagination.currentPage === serverPagination.totalPages || isPaginating}
                  className={`inline-flex items-center px-3 py-1 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity ${
                    isPaginating ? 'opacity-60' : ''
                  }`}
                >
                  Next
                  <ChevronRight size={16} className="ml-1" />
                </button>
                
                {/* Last page */}
                <button
                  onClick={() => handlePageChange(serverPagination.totalPages)}
                  disabled={serverPagination.currentPage === serverPagination.totalPages || isPaginating}
                  className={`px-3 py-1 text-sm bg-white border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-opacity ${
                    isPaginating ? 'opacity-60' : ''
                  }`}
                >
                  Last
                </button>
              </div>
            </div>
            
            {/* Page size selector */}
            <div className="mt-3 flex items-center gap-2">
              <span className="text-sm text-gray-700">Records per page:</span>
                          <select
              value={serverPagination.pageSize}
              onChange={async (e) => {
                const newPageSize = parseInt(e.target.value);
                setServerPagination(prev => ({ ...prev, pageSize: newPageSize, currentPage: 1 }));
                try {
                  setIsPaginating(true);
                  if (searchQuery) {
                    await searchSessions(searchQuery, 1);
                  } else {
                    await fetchSessions(1, newPageSize);
                  }
                } catch (error) {
                  toast.error('Failed to change page size');
                } finally {
                  setIsPaginating(false);
                }
              }}
              disabled={isPaginating}
              className={`text-sm border rounded px-2 py-1 bg-white transition-opacity disabled:cursor-not-allowed ${
                isPaginating ? 'opacity-60' : ''
              }`}
            >
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
                <option value={500}>500</option>
              </select>
            </div>
          </div>
        </div>

        <div className="mt-8">
          <Footer />
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Add Free Parker</h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleAddSession} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">License Plate</label>
                <input
                  type="text"
                  required
                  value={newSession.license_plate}
                  onChange={(e) => setNewSession({ ...newSession, license_plate: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Enter license plate"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Location</label>
                <select
                  value={newSession.location}
                  onChange={(e) => setNewSession({ ...newSession, location: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value={lot?.display_name || ''}>{lot?.display_name}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Notes</label>
                <textarea
                  value={newSession.notes}
                  onChange={(e) => setNewSession({ ...newSession, notes: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Enter any notes about this subscription (optional)"
                  rows={3}
                />
              </div>
              
              <button
                type="submit"
                className="w-full bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition duration-200"
              >
                Add Free Parker
              </button>
            </form>
          </div>
        </div>
      )}

      {isDeleteModalOpen && sessionToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="text-center">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Confirm Delete</h2>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete the {(userRole === 'admin' || userRole === 'owner') 
                  ? currentView === 'subscriptions' ? 'subscription' : currentView === 'free_parkers' ? 'free parker' : currentView === 'all' ? 'session' : 'parking session'
                  : 'parking session'} for license plate{' '}
                <span className="font-semibold">{sessionToDelete.license_plate}</span>?
              </p>
              <div className="flex gap-4">
                <button
                  onClick={() => setIsDeleteModalOpen(false)}
                  className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200 transition duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteSession}
                  className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition duration-200"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isEditModalOpen && sessionToEdit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Edit Session</h2>
              <button
                onClick={() => setIsEditModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleEditSession} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  License Plate
                </label>
                <input
                  type="text"
                  value={editLicensePlate}
                  onChange={e => setEditLicensePlate(e.target.value.toUpperCase())}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 uppercase"
                  placeholder="e.g. ABC 123"
                  required
                  disabled={editSaving}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={editPhone}
                  onChange={e => setEditPhone(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. 6045551234"
                  disabled={editSaving}
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setIsEditModalOpen(false)}
                  disabled={editSaving}
                  className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200 transition duration-200 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={editSaving}
                  className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition duration-200 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {editSaving ? <><Loader2 size={16} className="animate-spin" /> Saving…</> : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isCancelSubModalOpen && sessionToCancelSub && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Cancel Subscription</h2>
              <button
                onClick={() => setIsCancelSubModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>

            <p className="text-gray-600 mb-5">
              Choose how to cancel the subscription for license plate{' '}
              <span className="font-semibold">{sessionToCancelSub.license_plate}</span>.
              {sessionToCancelSub.email && (
                <> A cancellation notice will be sent to <span className="font-semibold">{sessionToCancelSub.email}</span>.</>
              )}
            </p>

            <div className="space-y-3 mb-6">
              <label className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-colors ${cancelSubMode === 'end_of_period' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
                <input
                  type="radio"
                  name="cancelMode"
                  value="end_of_period"
                  checked={cancelSubMode === 'end_of_period'}
                  onChange={() => setCancelSubMode('end_of_period')}
                  className="mt-0.5 accent-blue-600"
                />
                <div>
                  <p className="font-medium text-gray-900 text-sm">Cancel at end of billing period</p>
                  <p className="text-gray-500 text-xs mt-0.5">Subscription stays active until the current period ends. No refund issued.</p>
                </div>
              </label>

              <label className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-colors ${cancelSubMode === 'immediately' ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-gray-300'}`}>
                <input
                  type="radio"
                  name="cancelMode"
                  value="immediately"
                  checked={cancelSubMode === 'immediately'}
                  onChange={() => setCancelSubMode('immediately')}
                  className="mt-0.5 accent-orange-600"
                />
                <div>
                  <p className="font-medium text-gray-900 text-sm">Cancel immediately</p>
                  <p className="text-gray-500 text-xs mt-0.5">Subscription ends right now. No refund issued.</p>
                </div>
              </label>

              <label className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-colors ${cancelSubMode === 'immediately_refund' ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-gray-300'}`}>
                <input
                  type="radio"
                  name="cancelMode"
                  value="immediately_refund"
                  checked={cancelSubMode === 'immediately_refund'}
                  onChange={() => setCancelSubMode('immediately_refund')}
                  className="mt-0.5 accent-red-600"
                />
                <div>
                  <p className="font-medium text-gray-900 text-sm">Cancel immediately and refund last payment</p>
                  <p className="text-gray-500 text-xs mt-0.5">Subscription ends right now and the most recent payment is refunded.</p>
                </div>
              </label>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setIsCancelSubModalOpen(false)}
                disabled={cancelSubProcessing}
                className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200 transition duration-200 disabled:opacity-50"
              >
                Back
              </button>
              <button
                onClick={handleCancelSubscription}
                disabled={cancelSubProcessing}
                className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition duration-200 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {cancelSubProcessing ? <><Loader2 size={16} className="animate-spin" /> Processing…</> : 'Confirm Cancellation'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isRefundModalOpen && sessionToRefund && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="text-center">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Confirm Refund</h2>
              <p className="text-gray-600 mb-4">
                Are you sure you want to refund ${sessionToRefund.amount.toFixed(2)} for license plate{' '}
                <span className="font-semibold">{sessionToRefund.license_plate}</span>?
              </p>
              
              {sessionToRefund.payment_method_type === 'subscription' ? (
                <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-700 mb-4 text-left">
                  <p className="font-medium">Subscription Refund</p>
                  <p className="mt-1">This will cancel the subscription and issue a prorated refund for the remaining period.</p>
                </div>
              ) : (
                <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-700 mb-4 text-left">
                  <p className="font-medium">Payment Refund</p>
                  <p className="mt-1">This will process a full refund of the original payment.</p>
                </div>
              )}
              
              {!sessionToRefund.payment_id && (
                <div className="bg-yellow-50 p-3 rounded-lg text-sm text-yellow-700 mb-4 text-left">
                  <p className="font-medium">Warning</p>
                  <p className="mt-1">No payment information is available. This will only mark the session as refunded in the system.</p>
                </div>
              )}
              
              <div className="flex gap-4">
                <button
                  onClick={() => setIsRefundModalOpen(false)}
                  className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200 transition duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRefund}
                  className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition duration-200"
                >
                  Refund
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;